import React from 'react';
import { motion } from 'framer-motion';
import { 
  TrendingUp, 
  Shield, 
  BarChart3, 
  Smartphone, 
  DollarSign, 
  Zap,
  Globe,
  Users,
  Award,
  HeadphonesIcon,
  BookOpen,
  Lock,
  CheckCircle,
  Target,
  Bitcoin
} from 'lucide-react';

// Import professional images
const servicesHeroImage = require('../../assets/diverse-teammates-examining-statistics-infographics-computer_482257-90942.jpg_z2rlyq.jpg');
const analyticsImage = require('../../assets/analyst-team-utilize-bi-fintech-analyze-financial-data-ornamented-meeting_31965-377645.jpg_hqqshc.jpg');

const Services = () => {
  const tradingServices = [
    {
      icon: <TrendingUp className="h-8 w-8 text-emerald-600" />,
      title: 'Stock Trading',
      description: 'Trade stocks from major exchanges worldwide with real-time data and zero commission fees.',
      features: [
        'Zero commission stock trades',
        'Real-time market data',
        'Advanced order types',
        'After-hours trading',
        'Fractional shares',
        'Dividend reinvestment'
      ],
      color: 'emerald'
    },
    {
      icon: <Bitcoin className="h-8 w-8 text-orange-600" />,
      title: 'Cryptocurrency Trading',
      description: 'Access 100+ cryptocurrencies with secure storage and instant trading capabilities.',
      features: [
        '100+ cryptocurrencies',
        'Secure cold storage',
        'Real-time price feeds',
        'Advanced order types',
        'Staking rewards',
        'DeFi integration'
      ],
      color: 'orange'
    },
    {
      icon: <BarChart3 className="h-8 w-8 text-blue-600" />,
      title: 'Options Trading',
      description: 'Access sophisticated options strategies with professional-grade tools and analytics.',
      features: [
        'Options chains & analytics',
        'Strategy builder',
        'Risk management tools',
        'Paper trading',
        'Greeks analysis',
        'Profit/loss calculator'
      ],
      color: 'blue'
    },
    {
      icon: <DollarSign className="h-8 w-8 text-purple-600" />,
      title: 'Forex Trading',
      description: 'Trade major currency pairs with tight spreads and 24/5 market access.',
      features: [
        'Major & minor pairs',
        'Tight spreads',
        '24/5 trading',
        'Leverage up to 1:100',
        'Economic calendar',
        'Currency analytics'
      ],
      color: 'purple'
    },
    {
      icon: <Globe className="h-8 w-8 text-indigo-600" />,
      title: 'Global Markets',
      description: 'Access international markets including commodities, indices, and bonds.',
      features: [
        'International exchanges',
        'Commodities trading',
        'Index funds & ETFs',
        'Government bonds',
        'Multi-currency accounts',
        'Local market access'
      ],
      color: 'indigo'
    },
    {
      icon: <Zap className="h-8 w-8 text-yellow-600" />,
      title: 'Algorithmic Trading',
      description: 'Build and deploy trading algorithms with our powerful API and backtesting tools.',
      features: [
        'Strategy development',
        'Backtesting engine',
        'Live trading API',
        'Performance analytics',
        'Risk management',
        'Automated execution'
      ],
      color: 'yellow'
    }
  ];

  const platformServices = [
    {
      icon: <Smartphone className="h-8 w-8 text-emerald-600" />,
      title: 'Mobile Trading App',
      description: 'Professional trading on the go with our award-winning mobile applications.',
      features: [
        'iOS & Android apps',
        'Full trading functionality',
        'Real-time notifications',
        'Biometric security',
        'Offline chart viewing',
        'Social trading features'
      ],
      color: 'emerald'
    },
    {
      icon: <BarChart3 className="h-8 w-8 text-blue-600" />,
      title: 'Advanced Analytics',
      description: 'Professional-grade charting and market analysis tools for informed trading.',
      features: [
        '100+ technical indicators',
        'Custom chart layouts',
        'Pattern recognition',
        'Market screeners',
        'Price alerts',
        'Trading signals'
      ],
      color: 'blue'
    },
    {
      icon: <Shield className="h-8 w-8 text-purple-600" />,
      title: 'Risk Management',
      description: 'Comprehensive risk management tools to protect your investments.',
      features: [
        'Position sizing tools',
        'Stop-loss automation',
        'Portfolio risk analysis',
        'Margin monitoring',
        'Diversification alerts',
        'Stress testing'
      ],
      color: 'purple'
    }
  ];

  const supportServices = [
    {
      icon: <HeadphonesIcon className="h-8 w-8 text-green-600" />,
      title: '24/7 Customer Support',
      description: 'Round-the-clock support from our expert team via phone, chat, and email.',
      features: [
        '24/7 live chat',
        'Phone support',
        'Email assistance',
        'Video tutorials',
        'Dedicated account managers',
        'Multi-language support'
      ],
      color: 'green'
    },
    {
      icon: <BookOpen className="h-8 w-8 text-orange-600" />,
      title: 'Educational Resources',
      description: 'Comprehensive trading education to help you improve your skills and knowledge.',
      features: [
        'Trading courses',
        'Webinars & seminars',
        'Market analysis',
        'Trading guides',
        'Demo accounts',
        'Trading simulator'
      ],
      color: 'orange'
    },
    {
      icon: <Users className="h-8 w-8 text-indigo-600" />,
      title: 'Community Platform',
      description: 'Connect with other traders, share strategies, and learn from the community.',
      features: [
        'Trading forums',
        'Strategy sharing',
        'Social trading',
        'Market discussions',
        'Expert insights',
        'Trading contests'
      ],
      color: 'indigo'
    }
  ];

  const stats = [
    { icon: Users, number: '1M+', label: 'Active Traders' },
    { icon: Globe, number: '150+', label: 'Countries Served' },
    { icon: TrendingUp, number: '$50B+', label: 'Annual Volume' },
    { icon: Award, number: '15+', label: 'Years Experience' }
  ];

  return (
    <div className="services-page">
      {/* Hero Section */}
      <section className="services-hero">
        <div className="services-hero-container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            
            {/* Left Content */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="services-hero-content"
            >
              <div className="services-hero-badge">
                <Target className="services-hero-badge-icon" />
                <span>Our Services</span>
              </div>
              <h1 className="services-hero-title">
                Comprehensive
                <span className="services-hero-title-gradient"> Trading Solutions</span>
              </h1>
              <p className="services-hero-subtitle">
                From stocks to cryptocurrencies, we provide the tools, technology, and expertise 
                you need to succeed in today's dynamic financial markets.
              </p>
              <motion.a
                href="https://dashboard.chartlinebrokerage.com"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="services-hero-cta"
              >
                Explore All Services
              </motion.a>
            </motion.div>

            {/* Right Image */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="services-hero-image"
            >
              <div className="relative">
                <img
                  src={servicesHeroImage}
                  alt="Trading Services"
                  className="w-full h-auto rounded-2xl shadow-2xl"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-blue-900/20 to-transparent rounded-2xl" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Trading Services Section */}
      <section className="services-trading-section">
        <div className="services-trading-container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="services-trading-header"
          >
            <h2 className="services-trading-title">Trading Services</h2>
            <p className="services-trading-subtitle">
              Access global markets with our comprehensive trading solutions
            </p>
          </motion.div>

          <div className="services-trading-grid">
            {tradingServices.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="services-card group"
              >
                <div className="services-card-glow"></div>
                
                <div className="services-card-content">
                  <div className={`services-card-icon ${service.color}`}>
                    {service.icon}
                  </div>
                  
                  <h3 className="services-card-title">
                    {service.title}
                  </h3>
                  
                  <p className="services-card-description">
                    {service.description}
                  </p>
                  
                  <ul className="services-card-features">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="services-card-feature">
                        <CheckCircle className="services-card-feature-icon" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  
                  <div className="services-card-border"></div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Platform Services Section */}
      <section className="services-platform-section">
        <div className="services-platform-container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="services-platform-header"
          >
            <h2 className="services-platform-title">Platform & Technology</h2>
            <p className="services-platform-subtitle">
              Advanced tools and technology to enhance your trading experience
            </p>
          </motion.div>

          <div className="services-platform-grid">
            {platformServices.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="services-card group"
              >
                <div className="services-card-glow"></div>
                
                <div className="services-card-content">
                  <div className={`services-card-icon ${service.color}`}>
                    {service.icon}
                  </div>
                  
                  <h3 className="services-card-title">
                    {service.title}
                  </h3>
                  
                  <p className="services-card-description">
                    {service.description}
                  </p>
                  
                  <ul className="services-card-features">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="services-card-feature">
                        <CheckCircle className="services-card-feature-icon" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  
                  <div className="services-card-border"></div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Support Services Section */}
      <section className="services-support-section">
        <div className="services-support-container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="services-support-header"
          >
            <h2 className="services-support-title">Support & Education</h2>
            <p className="services-support-subtitle">
              Comprehensive support and educational resources for your trading journey
            </p>
          </motion.div>

          <div className="services-support-grid">
            {supportServices.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="services-card group"
              >
                <div className="services-card-glow"></div>
                
                <div className="services-card-content">
                  <div className={`services-card-icon ${service.color}`}>
                    {service.icon}
                  </div>
                  
                  <h3 className="services-card-title">
                    {service.title}
                  </h3>
                  
                  <p className="services-card-description">
                    {service.description}
                  </p>
                  
                  <ul className="services-card-features">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="services-card-feature">
                        <CheckCircle className="services-card-feature-icon" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  
                  <div className="services-card-border"></div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Analytics Feature Section */}
      <section className="services-analytics-section">
        <div className="services-analytics-container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            
            {/* Left Image */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="services-analytics-image"
            >
              <img
                src={analyticsImage}
                alt="Advanced Analytics"
                className="w-full h-auto rounded-2xl shadow-xl"
              />
            </motion.div>

            {/* Right Content */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="services-analytics-content"
            >
              <h2 className="services-analytics-title">
                Professional <span className="text-blue-600">Analytics</span>
              </h2>
              <p className="services-analytics-subtitle">
                Make informed trading decisions with our comprehensive suite of analytical tools 
                and market intelligence designed for serious traders.
              </p>
              
              <div className="services-analytics-features">
                <div className="services-analytics-feature">
                  <BarChart3 className="services-analytics-feature-icon" />
                  <div>
                    <h4>Advanced Charting</h4>
                    <p>Professional charts with 100+ technical indicators</p>
                  </div>
                </div>
                <div className="services-analytics-feature">
                  <Target className="services-analytics-feature-icon" />
                  <div>
                    <h4>Market Insights</h4>
                    <p>Real-time market analysis and trading opportunities</p>
                  </div>
                </div>
                <div className="services-analytics-feature">
                  <TrendingUp className="services-analytics-feature-icon" />
                  <div>
                    <h4>Portfolio Analytics</h4>
                    <p>Comprehensive portfolio performance tracking</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="services-stats-section">
        <div className="services-stats-container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="services-stats-header"
          >
            <h2 className="services-stats-title">
              Trusted by Traders Worldwide
            </h2>
            <p className="services-stats-subtitle">
              Join millions of successful traders who choose ChartLine for their trading needs
            </p>
          </motion.div>

          <div className="services-stats-grid">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.8 + index * 0.1 }}
                className="services-stat-card"
              >
                <div className="services-stat-icon">
                  <stat.icon className="h-8 w-8 text-emerald-600" />
                </div>
                <div className="services-stat-content">
                  <div className="services-stat-number">{stat.number}</div>
                  <div className="services-stat-label">{stat.label}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="services-cta-section">
        <div className="services-cta-container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="services-cta-content"
          >
            <h2 className="services-cta-title">
              Ready to Experience Our Services?
            </h2>
            <p className="services-cta-subtitle">
              Open your account today and access our comprehensive trading platform with all services included
            </p>
            <div className="services-cta-buttons">
              <motion.a
                href="https://dashboard.chartlinebrokerage.com"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="services-cta-primary"
              >
                Open Trading Account
              </motion.a>
              <motion.a
                href="https://dashboard.chartlinebrokerage.com"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="services-cta-secondary"
              >
                Try Demo Account
              </motion.a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Services; 